import React, { useState, useEffect } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { StylishSelector } from '@/components/checkboxes/StylishSelector';
import DimensionsSelector from '@/components/image-generator/FilterBar/components/DimensionsSelector';
import { Camera, PenTool, Origami, Box } from 'lucide-react';
import { useWorkbookImageGeneration } from '@/hooks/content-generator/use-workbook-images';

// Style options matching cover images exactly
const styleOptions = [
  { id: 'style-photo', name: 'Photo', icon: Camera },
  { id: 'style-art', name: 'Art', icon: PenTool },
  { id: 'style-anime', name: 'Anime', icon: Origami },
  { id: 'style-3d', name: '3D', icon: Box }
];

interface WorkbookImageGenerationContentProps {
  setDescription?: (desc: string) => void;
  setStyle?: (style: string) => void;
  setWidth?: (width: string) => void;
  setHeight?: (height: string) => void;
  initialDescription?: string; // NEW: Pre-fill description with task data
  initialStyle?: string; // NEW: Pre-fill style with smart detection
}

const WorkbookImageGenerationContent: React.FC<WorkbookImageGenerationContentProps> = ({ 
  setDescription,
  setStyle,
  setWidth,
  setHeight,
  initialDescription = '', // NEW: Destructure initial values
  initialStyle = 'style-photo'
}) => {
  // Debug logging for initial props
  React.useEffect(() => {
    console.log('[WorkbookImageGenerationContent] Received props:', {
      initialDescription,
      initialStyle
    });
  }, [initialDescription, initialStyle]);
  
  const [description, setLocalDescription] = useState(initialDescription); // NEW: Initialize with prop
  const [selectedStyle, setSelectedStyle] = useState<string>(initialStyle); // NEW: Initialize with prop
  const [width, setLocalWidth] = useState<string>('1024');
  const [height, setLocalHeight] = useState<string>('1024');
  
  // Update parent component whenever values change
  useEffect(() => {
    if (setDescription) {
      setDescription(description);
    }
    if (setStyle) {
      setStyle(selectedStyle);
    }
    if (setWidth) {
      setWidth(width);
    }
    if (setHeight) {
      setHeight(height);
    }
  }, [description, selectedStyle, width, height, setDescription, setStyle, setWidth, setHeight]);
  
  const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newDescription = e.target.value;
    setLocalDescription(newDescription);
  };
  
  const handleStyleChange = (style: string) => {
    setSelectedStyle(style);
  };

  const handleWidthChange = (newWidth: string | string[]) => {
    const widthValue = Array.isArray(newWidth) ? newWidth[0] : newWidth;
    setLocalWidth(widthValue);
  };

  const handleHeightChange = (newHeight: string | string[]) => {
    const heightValue = Array.isArray(newHeight) ? newHeight[0] : newHeight;
    setLocalHeight(heightValue);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Textarea
          id="description"
          placeholder="Describe your workbook image (e.g., a professional diagram, flowchart, or illustration)"
          value={description}
          onChange={handleDescriptionChange}
          className="min-h-[100px] resize-none"
        />
      </div>

      <div className="space-y-2">
        <h3 className="text-sm font-medium mb-1 text-purple-600 dark:text-purple-400">Image Type</h3>
        <StylishSelector
          styles={styleOptions}
          selectedStyle={selectedStyle}
          onSelectStyle={handleStyleChange}
          className="grid-cols-2 gap-4"
          colorVariant="purple"
          checkColor="purple-500"
        />
      </div>

      {/* Add Dimensions Selector */}
      <div className="space-y-2">
        <DimensionsSelector
          width={width}
          height={height}
          onWidthChange={handleWidthChange}
          onHeightChange={handleHeightChange}
        />
      </div>
    </div>
  );
};

export default WorkbookImageGenerationContent; 